import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Crown, MapPin, Sparkles, Users } from 'lucide-react'
import { OpenStatus } from '@/components/generals/open-status'
import { CallButton, WhatsAppButton } from '@/components/generals/cta-buttons'
import { address, hours, siteConfig } from '@/constants/site'

const chipClass = 'inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-white/5 px-3 py-1 text-xs font-medium text-ivory/90'

export const HeroSection: FC = (): ReactNode => {
    return (
        <section
            id='home'
            aria-labelledby='hero-title'
            className='plum-glow relative isolate overflow-hidden text-ivory'
        >
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 top-32 -z-10 size-96 rounded-full bg-plum/70 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute -right-24 -top-32 -z-10 size-[28rem] rounded-full bg-gold/10 blur-3xl' />

            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-10 sm:px-8 sm:pt-14 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10 lg:pb-28 lg:pt-16'>
                <div className='text-center lg:text-left'>
                    <p className='inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/5 px-4 py-1.5 text-[0.65rem] font-medium uppercase tracking-[0.2em] text-gold-light sm:text-xs'>
                        <Sparkles className='size-3.5 shrink-0' aria-hidden='true' />
                        <span className='sm:hidden'>Women's Beauty Parlour, Delhi</span>
                        <span className='hidden sm:inline'>Women's Beauty Parlour in New Gupta Colony, Delhi</span>
                    </p>

                    <h1 id='hero-title' className='mt-7'>
                        <span className='gold-shine block px-1 font-heading text-[2.85rem] font-bold leading-[1.05] tracking-[0.08em] sm:text-7xl lg:text-[5.25rem]'>
                            Anushka
                        </span>
                        {' '}
                        <span className='gold-shine mt-2 block px-1 font-heading text-lg font-semibold tracking-[0.36em] sm:text-2xl lg:text-[1.7rem]'>
                            Beauty Parlour
                        </span>
                    </h1>

                    <p className='gold-shine mt-3 inline-block px-3 pb-2 font-script text-[2.6rem] leading-[1.15] sm:text-5xl lg:text-[3.6rem]'>
                        {siteConfig.tagline}
                    </p>

                    <p className='mx-auto mt-5 max-w-xl text-base leading-relaxed text-ivory/80 sm:text-lg lg:mx-0'>
                        Bridal makeups, party makeovers, skin treatments, nail art, facial cleanup and hair designs, by experienced artists who truly care about your skin.
                    </p>

                    <div className='mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center lg:justify-start'>
                        <WhatsAppButton variant='gold' label='Book on WhatsApp' />
                        <CallButton variant='outline-light' />
                    </div>

                    <ul className='mt-8 flex flex-wrap items-center justify-center gap-2.5 lg:justify-start'>
                        <li>
                            <OpenStatus tone='dark' />
                        </li>

                        <li className={chipClass}>
                            Daily {hours.shortLabel}
                        </li>

                        <li className={chipClass}>
                            <Users className='size-3.5 text-gold-light' aria-hidden='true' />
                            Women only
                        </li>

                        <li className={chipClass}>
                            <MapPin className='size-3.5 text-gold-light' aria-hidden='true' />
                            {address.shop}, {address.locality}
                        </li>
                    </ul>
                </div>

                <div className='relative mx-auto w-full max-w-[21rem] sm:max-w-md lg:max-w-[30rem]'>
                    <div aria-hidden='true' className='absolute -inset-3 rounded-t-full rounded-b-[2rem] border border-gold/35 sm:-inset-4' />

                    <div className='relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-[1.75rem] border-2 border-gold/70 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.85)]'>
                        <Image
                            src='/images/hero-bride.jpg'
                            alt='Indian bride in a red embroidered lehenga with gold bridal jewellery'
                            fill
                            preload
                            sizes='(min-width: 1024px) 30rem, (min-width: 640px) 28rem, 21rem'
                            className='object-cover object-[50%_8%]'
                        />

                        <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-plum-deep/70 to-transparent' />
                    </div>

                    <div className='animate-soft-float absolute -left-3 bottom-10 flex items-center gap-3 rounded-2xl border border-gold/30 bg-plum-deep/90 px-4 py-3 shadow-xl shadow-black/40 backdrop-blur sm:-left-10'>
                        <span className='gold-gradient flex size-10 items-center justify-center rounded-full text-plum-deep'>
                            <Crown className='size-5' aria-hidden='true' />
                        </span>

                        <span className='text-left'>
                            <span className='block text-[0.65rem] uppercase tracking-[0.2em] text-gold-light/80'>
                                Bridal
                            </span>
                            <span className='block font-heading text-sm font-semibold text-ivory'>
                                HD &amp; Airbrush
                            </span>
                        </span>
                    </div>

                    <div className='animate-soft-float absolute -right-3 top-20 flex items-center gap-3 rounded-2xl border border-gold/30 bg-white/95 px-3 py-2.5 text-plum shadow-xl shadow-black/30 [animation-delay:1.6s] sm:-right-10'>
                        <span className='relative size-11 overflow-hidden rounded-full border-2 border-gold/60'>
                            <Image
                                src='/images/nail-extensions.jpg'
                                alt=''
                                fill
                                sizes='44px'
                                className='object-cover'
                            />
                        </span>

                        <span className='text-left'>
                            <span className='block text-[0.65rem] uppercase tracking-[0.2em] text-gold-dark'>
                                Designer
                            </span>
                            <span className='block font-heading text-sm font-semibold'>
                                Nail Art
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='gold-hairline absolute inset-x-0 bottom-0 h-px' />
        </section>
    )
}
